import ButtonBasic from "@rentiohq/shared-frontend/dist/components/components/ButtonBasic";
import ButtonPrimary, {
  EButtonSize,
} from "@rentiohq/shared-frontend/dist/components/components/ButtonPrimary";
import Spacer, {
  ESpacerWeight,
} from "@rentiohq/shared-frontend/dist/components/components/Spacer";
import { useCountDown } from "@rentiohq/shared-frontend/dist/hooks/useCountDown";
import * as authActions from "@rentiohq/shared-frontend/dist/redux/auth/auth.actions";
import * as authSelectorsV1 from "@rentiohq/shared-frontend/dist/redux/auth/auth.selectors";
import * as authActionsV2 from "@rentiohq/shared-frontend/dist/reduxV2/auth/auth.actions";
import * as authSelectors from "@rentiohq/shared-frontend/dist/reduxV2/auth/auth.selectors";
import { showAlert } from "@rentiohq/shared-frontend/dist/utils/alert/alert.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { htmlTextAsReactText } from "@rentiohq/shared-frontend/dist/utils/text/text.utils";
import { default as React } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router";
import { Navigate } from "react-router-dom";
import { IRootState } from "redux/reducers";
import InfoHeader from "../../../../components/InfoHeader";
import Page from "../../../../components/Page";
import { getReferrer } from "../../../../utils/navigation.utils";
import * as S from "./VerifyEmail.styles";

const COUNT_LIMIT /* in milliseconds */ = 60_000;

export const VerifyEmail = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const { remainingCountDownInSeconds, startCountdown } =
    useCountDown(COUNT_LIMIT);

  const user = useSelector((state: IRootState) =>
    authSelectorsV1.getUser(state),
  );
  const hasAccessToken = useSelector(authSelectors.hasAccessToken);

  const [preventRedirect, setPreventRedirect] = React.useState(false);

  // Event handlers
  const handlePressVerifiedEmail = () => {
    setPreventRedirect(false);
    if (hasAccessToken) {
      dispatch(authActions.signUpVerifiedEmail());
    } else if (user) {
      dispatch(
        authActionsV2.signUpRequestTokenFromEmailStart.getAction({
          userId: user.id,
          token: user.unverifiedEmailRequestToken,
        }),
      );
    } else {
      showAlert({ type: "error", message: getLocalizedText("general.error") });
    }
  };

  const handlePressResendEmail = () => {
    startCountdown();
    dispatch(authActions.signUpResendVerificationEmail());
  };

  const handlePressSignIn = () => {
    dispatch(authActionsV2.signOutStart.getAction());

    navigate("/auth/signin", {
      state: { referrer: getReferrer(location) },
    });
  };

  // Helpers
  const getEmail = () => {
    if (!user) {
      return;
    }

    return user.unverifiedEmail;
  };

  // Render
  const renderRedirect = () => {
    if (preventRedirect) {
      return null;
    }

    if (!user || user.unverifiedEmail) {
      return null;
    }

    return <Navigate to={getReferrer(location)} />;
  };

  return (
    <>
      {renderRedirect()}
      <Page>
        <InfoHeader
          title={getLocalizedText("verify_email.info.title")}
          body={htmlTextAsReactText(
            getLocalizedText("verify_email.info.description", {
              email: getEmail() || "",
            }),
          )}
        />
        <Spacer weight={ESpacerWeight.W24} />
        <S.ButtonsWrap>
          <ButtonPrimary
            title={getLocalizedText("verify_email.cta.done")}
            buttonSize={EButtonSize.Full}
            onPress={handlePressVerifiedEmail}
          />

          <Spacer weight={ESpacerWeight.W16} />

          <S.ButtonInfo>
            <span>{getLocalizedText("verify_email.title.resend_email")} </span>
            <ButtonBasic
              title={`${getLocalizedText("verify_email.cta.resend_email")}${
                remainingCountDownInSeconds > 0
                  ? ` (${remainingCountDownInSeconds})`
                  : ""
              }`}
              onPress={handlePressResendEmail}
              disabled={remainingCountDownInSeconds > 0}
            />
          </S.ButtonInfo>

          <Spacer weight={ESpacerWeight.W04} />

          <S.ButtonInfo>
            <span>
              {getLocalizedText("verify_email.cta.update_email.disclosure")}{" "}
            </span>
            <ButtonBasic
              title={getLocalizedText("verify_email.cta.update_email")}
              onPress={() => navigate("/auth/edit-email")}
            />
          </S.ButtonInfo>

          <Spacer weight={ESpacerWeight.W04} />

          <S.ButtonInfo>
            <span>{getLocalizedText("auth.sign_up.login.title")} </span>
            <ButtonBasic
              title={getLocalizedText("auth.sign_up.login.cta")}
              onPress={handlePressSignIn}
            />
          </S.ButtonInfo>
        </S.ButtonsWrap>
      </Page>
    </>
  );
};

// eslint-disable-next-line import/no-default-export
export default VerifyEmail;
